import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select } from "../components/DesignSystem";
import type { Favour, FavourRecipientRule } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";
import { RequestQuoteAction } from "./RequestQuoteAction";

const RULE_LABELS: Record<FavourRecipientRule, string> = { per_guest: "Per guest", per_household: "Per household", per_event_attendee: "Per event attendee" };

export function GiftsScreen() {
  const { workspace, loading, error, service } = usePlannerData();
  const [favours, setFavours] = React.useState<Favour[] | null>(null);
  const [favoursLoading, setFavoursLoading] = React.useState(false);
  const [favoursError, setFavoursError] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [overriding, setOverriding] = React.useState<Favour | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setFavoursLoading(true);
    setFavoursError(null);
    try {
      setFavours(await service.favours.list());
    } catch (err) {
      setFavoursError(errorMessage(err));
    } finally {
      setFavoursLoading(false);
    }
  }, [service]);

  React.useEffect(() => { void load(); }, [load]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading gifts & favours" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const toggle = async (favour: Favour, field: "ordered" | "received" | "distributed") => {
    setActionError(null);
    setBusyId(favour.id);
    try { await service.favours.updateFavour(favour.id, { [field]: !favour[field] }); await load(); } catch (err) { setActionError(errorMessage(err)); } finally { setBusyId(null); }
  };
  const reset = async (favour: Favour) => {
    setActionError(null);
    try { await service.favours.resetToCalculated(favour.id); await load(); } catch (err) { setActionError(errorMessage(err)); }
  };
  const remove = async (favour: Favour) => {
    setActionError(null);
    try { await service.favours.deleteFavour(favour.id); await load(); } catch (err) { setActionError(errorMessage(err)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="More"
        title="Gifts & favours"
        actions={<>
          <RequestQuoteAction defaultItems={["Favours & gifts"]} />
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add favour</Button>
        </>}
      />
      <div className="pob-pad">
        {favoursLoading && !favours && <LoadingState label="Loading gifts & favours" />}
        {favoursError && <ErrorState message={favoursError} retry={load} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginBottom: "var(--space-4)" }}>{actionError}</Callout>}
        {favours && (
          <Card padding="0">
            <DataTable
              rows={favours}
              emptyLabel="No favours yet"
              columns={[
                { key: "itemName", header: "Item" },
                { key: "eventName", header: "Celebration", muted: true, render: (row) => row.eventName || "Whole wedding" },
                { key: "recipientRule", header: "Basis", width: 130, muted: true, render: (row) => RULE_LABELS[row.recipientRule] },
                { key: "quantity", header: "Quantity", width: 110, render: (row) => (
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ font: "var(--type-label)" }}>{row.manualOverride ? row.overrideQuantity : row.calculatedQuantity}</span>
                    {row.manualOverride && <Badge tone="accent">Manual</Badge>}
                  </span>
                ) },
                { key: "status", header: "Status", width: 220, render: (row) => (
                  <span style={{ display: "flex", gap: 4 }}>
                    <Button size="sm" variant={row.ordered ? "primary" : "secondary"} disabled={busyId === row.id} onClick={() => toggle(row, "ordered")}>Ordered</Button>
                    <Button size="sm" variant={row.received ? "primary" : "secondary"} disabled={busyId === row.id} onClick={() => toggle(row, "received")}>Received</Button>
                    <Button size="sm" variant={row.distributed ? "primary" : "secondary"} disabled={busyId === row.id} onClick={() => toggle(row, "distributed")}>Distributed</Button>
                  </span>
                ) },
                { key: "actions", header: "", width: 200, render: (row) => (
                  <span style={{ display: "flex", gap: 4 }}>
                    <Button size="sm" onClick={() => setOverriding(row)}>Override</Button>
                    {row.manualOverride && <Button size="sm" onClick={() => reset(row)}>Reset</Button>}
                    <Button size="sm" variant="danger" onClick={() => remove(row)}>Remove</Button>
                  </span>
                ) }
              ]}
            />
          </Card>
        )}
      </div>
      {adding && <AddFavourDialog onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await load(); }} />}
      {overriding && <OverrideDialog favour={overriding} onClose={() => setOverriding(null)} onSaved={async () => { setOverriding(null); await load(); }} />}
    </div>
  );
}

function AddFavourDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { service, workspace } = usePlannerData();
  const [itemName, setItemName] = React.useState("");
  const [eventId, setEventId] = React.useState("");
  const [vendorId, setVendorId] = React.useState("");
  const [recipientRule, setRecipientRule] = React.useState<FavourRecipientRule>("per_guest");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!itemName.trim()) { setFormError("Name the favour item."); return; }
    try {
      await service.favours.createFavour({ itemName: itemName.trim(), eventId: eventId || undefined, vendorId: vendorId || undefined, recipientRule }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog title="Add favour" onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Item"><Input value={itemName} onChange={(e) => setItemName(e.target.value)} placeholder="Personalized candle" /></Field>
        <Field label="Celebration (optional)"><Select value={eventId} placeholder="Whole wedding" onChange={(e) => setEventId(e.target.value)} options={workspace!.events.map((ev) => ({ value: ev.id, label: ev.name }))} /></Field>
        <Field label="Vendor (optional)"><Select value={vendorId} placeholder="None" onChange={(e) => setVendorId(e.target.value)} options={workspace!.vendors.map((v) => ({ value: v.id, label: v.name }))} /></Field>
        <Field label="Quantity basis"><Select value={recipientRule} onChange={(e) => setRecipientRule(e.target.value as FavourRecipientRule)} options={[{ value: "per_guest", label: "Per guest" }, { value: "per_household", label: "Per household" }, { value: "per_event_attendee", label: "Per event attendee" }]} /></Field>
      </div>
    </Dialog>
  );
}

function OverrideDialog({ favour, onClose, onSaved }: { favour: Favour; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [quantity, setQuantity] = React.useState(String(favour.manualOverride ? favour.overrideQuantity : favour.calculatedQuantity));
  const [reason, setReason] = React.useState(favour.overrideReason || "");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    const parsed = Number(quantity);
    if (!Number.isFinite(parsed) || parsed < 0) { setFormError("Enter a quantity of 0 or more."); return; }
    try { await service.favours.setOverride(favour.id, parsed, reason.trim() || undefined); await onSaved(); } catch (err) { setFormError(errorMessage(err)); }
  });

  return (
    <Dialog title={`Override — ${favour.itemName}`} description={`Calculated quantity is currently ${favour.calculatedQuantity}. A manual quantity is never silently recalculated.`} onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Save"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Quantity"><Input value={quantity} type="number" onChange={(e) => setQuantity(e.target.value)} /></Field>
        <Field label="Reason (optional)"><Input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </div>
    </Dialog>
  );
}
